/**
 * Chart utilities for Analytics component
 * Includes chart configuration options and Chart.js customizations
 */

import { ChartOptions } from 'chart.js';
import * as Chart from 'chart.js';
import { CHART_CORNER_RADIUS } from '../_constants/analytics.constants';
import { truncateChartLabel } from './analytics-formatters.util';

// ============================================================================
// Chart Configuration Options
// ============================================================================

const COMMON_LEGEND_CONFIG = {
  display: true,
  position: 'bottom' as const,
  labels: {
    boxWidth: 12,
    padding: 10,
    fontColor: '#374151',
    fontSize: 12
  }
};

const COMMON_TOOLTIP_CONFIG = {
  mode: 'index' as const,
  intersect: false,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  titleFontSize: 12,
  bodyFontSize: 11,
  padding: 8
};

const COMMON_Y_AXIS_CONFIG = {
  ticks: {
    beginAtZero: true,
    fontColor: '#6b7280',
    fontSize: 11
  },
  gridLines: {
    color: '#e5e7eb'
  }
};

const COMMON_X_AXIS_CONFIG = {
  ticks: {
    fontColor: '#6b7280',
    fontSize: 11,
    callback: function(value: string | number, index: number, values: any[]) {
      // Truncate labels for display only (tooltips will use original labels)
      if (typeof value === 'string') {
        return truncateChartLabel(value);
      }
      return value;
    }
  },
  gridLines: {
    display: false
  }
};

function createYAxisConfig() {
  return {
    ...COMMON_Y_AXIS_CONFIG,
    ticks: {
      ...COMMON_Y_AXIS_CONFIG.ticks
    },
    gridLines: {
      ...COMMON_Y_AXIS_CONFIG.gridLines
    }
  };
}

function createXAxisConfig() {
  return {
    ...COMMON_X_AXIS_CONFIG,
    ticks: {
      ...COMMON_X_AXIS_CONFIG.ticks
    },
    gridLines: {
      ...COMMON_X_AXIS_CONFIG.gridLines
    }
  };
}

/**
 * Get base bar chart options
 */
export function getBarChartOptions(): ChartOptions {
  return {
    responsive: true,
    maintainAspectRatio: false,
    legend: COMMON_LEGEND_CONFIG,
    tooltips: COMMON_TOOLTIP_CONFIG,
    scales: {
      yAxes: [createYAxisConfig()],
      xAxes: [createXAxisConfig()]
    }
  };
}

/**
 * Get stacked bar chart options
 */
export function getStackedBarChartOptions(): ChartOptions {
  return {
    responsive: true,
    maintainAspectRatio: false,
    legend: COMMON_LEGEND_CONFIG,
    tooltips: COMMON_TOOLTIP_CONFIG,
    scales: {
      yAxes: [{
        ...createYAxisConfig(),
        stacked: true
      }],
      xAxes: [{
        ...createXAxisConfig(),
        stacked: true
      }]
    }
  };
}

/**
 * Get forecast bar chart options (no legend by default)
 */
export function getForecastBarChartOptions(showLegend: boolean = false): ChartOptions {
  return {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      ...COMMON_LEGEND_CONFIG,
      display: showLegend
    },
    tooltips: COMMON_TOOLTIP_CONFIG,
    scales: {
      yAxes: [createYAxisConfig()],
      xAxes: [createXAxisConfig()]
    }
  };
}

/**
 * Get line chart options
 */
export function getLineChartOptions(): ChartOptions {
  return {
    responsive: true,
    maintainAspectRatio: false,
    legend: COMMON_LEGEND_CONFIG,
    tooltips: COMMON_TOOLTIP_CONFIG,
    scales: {
      yAxes: [createYAxisConfig()],
      xAxes: [createXAxisConfig()]
    },
    elements: {
      point: {
        radius: 2,
        hoverRadius: 4
      },
      line: {
        tension: 0.4
      }
    }
  };
}

/**
 * Get doughnut chart options with percentage tooltips
 * Supports concentric circles when multiple datasets are provided
 */
export function getDoughnutChartOptions(concentric: boolean = false): ChartOptions {
  const baseOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      ...COMMON_LEGEND_CONFIG,
      usePointStyle: false,
      generateLabels: (chart: any) => {
        const data = chart.data;
        if (data.labels && data.labels.length && data.datasets && data.datasets.length) {
          // For concentric circles, only show legend for the first dataset (outer ring)
          const datasetIndex = concentric ? 0 : 0;
          const dataset = data.datasets[datasetIndex];
          const backgroundColorArray = Array.isArray(dataset.backgroundColor)
            ? dataset.backgroundColor
            : [dataset.backgroundColor];

          return data.labels.map((label: string, i: number) => {
            const fillStyle = backgroundColorArray[i] || backgroundColorArray[0] || '#cccccc';

            return {
              text: label,
              fillStyle: fillStyle,
              strokeStyle: dataset.borderColor || '#ffffff',
              lineWidth: dataset.borderWidth || 0,
              hidden: !data.datasets[datasetIndex].data || data.datasets[datasetIndex].data[i] === null || data.datasets[datasetIndex].data[i] === undefined,
              index: i
            };
          });
        }
        return [];
      }
    },
    tooltips: {
      ...COMMON_TOOLTIP_CONFIG,
      callbacks: {
        label: (tooltipItem: any, data: any) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const total = dataset.data.reduce((sum: number, val: number) => sum + (val || 0), 0);
          const value = dataset.data[tooltipItem.index] || 0;
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
          const label = data.labels[tooltipItem.index] || '';
          const periodLabel = dataset.label ? ` (${dataset.label})` : '';
          return `${label}: ${percentage}%${periodLabel}`;
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: false
    },
    elements: {
      arc: {
        borderWidth: 2
      }
    }
  };

  // For concentric circles, we need to adjust cutout percentage
  // Outer ring uses default, inner ring needs larger cutout
  if (concentric) {
    // This will be handled by the datasets' weight property if supported
    // Otherwise, we'll use a custom plugin approach
    return {
      ...baseOptions,
      cutoutPercentage: 50 // Base cutout for outer ring
    };
  }

  return {
    ...baseOptions,
    cutoutPercentage: 50
  };
}

// ============================================================================
// Chart.js Customizations
// ============================================================================

/**
 * Apply rounded corners to bar charts
 * This modifies Chart.js Rectangle.draw to add rounded top corners for vertical bars
 * For stacked bars, only the top segment gets rounded corners
 */
export function applyRoundedBarCorners(): void {
  const originalDraw = Chart.elements.Rectangle.prototype.draw;

  Chart.elements.Rectangle.prototype.draw = function() {
    const ctx = this._chart.ctx;
    const vm = this._view;
    const chart = this._chart;
    let left: number, right: number, top: number, bottom: number;
    let signX: number, signY: number;
    let borderSkipped: string;
    const borderWidth = vm.borderWidth;

    if (!vm.horizontal) {
      // Vertical bar
      left = vm.x - vm.width / 2;
      right = vm.x + vm.width / 2;
      top = vm.y;
      bottom = vm.base;
      signX = 1;
      signY = bottom > top ? 1 : -1;
      borderSkipped = vm.borderSkipped || 'bottom';
    } else {
      // Horizontal bar
      left = vm.base;
      right = vm.x;
      top = vm.y - vm.height / 2;
      bottom = vm.y + vm.height / 2;
      signX = right > left ? 1 : -1;
      signY = 1;
      borderSkipped = vm.borderSkipped || 'left';
    }

    // Adjust sizes for border width
    if (borderWidth) {
      const barSize = Math.min(Math.abs(left - right), Math.abs(top - bottom));
      const adjustedBorderWidth = borderWidth > barSize ? barSize : borderWidth;
      const halfStroke = adjustedBorderWidth / 2;
      const borderLeft = left + (borderSkipped !== 'left' ? halfStroke * signX : 0);
      const borderRight = right + (borderSkipped !== 'right' ? -halfStroke * signX : 0);
      const borderTop = top + (borderSkipped !== 'top' ? halfStroke * signY : 0);
      const borderBottom = bottom + (borderSkipped !== 'bottom' ? -halfStroke * signY : 0);

      if (borderLeft !== borderRight) {
        top = borderTop;
        bottom = borderBottom;
      }
      if (borderTop !== borderBottom) {
        left = borderLeft;
        right = borderRight;
      }
    }

    // Check if this is a stacked chart
    const options = chart.config && chart.config.options;
    const scales = options && options.scales;
    const yAxes = scales && scales.yAxes;
    const xAxes = scales && scales.xAxes;
    const yScale = scales && scales.y;
    const xScale = scales && scales.x;
    
    const isStacked = (yAxes && yAxes[0] && yAxes[0].stacked === true) ||
                      (xAxes && xAxes[0] && xAxes[0].stacked === true) ||
                      (yScale && yScale.stacked === true) ||
                      (xScale && xScale.stacked === true);

    // For stacked charts, check if this is the top segment
    let isTopSegment = true;
    if (isStacked && !vm.horizontal) {
      // For vertical stacked bars, find all segments at the same x position
      // The top segment is the one with the smallest y value (closest to top of chart)
      const currentTop = top;
      const currentIndex = this._index;
      
      // Get all segments at the same x position (same data index)
      // In stacked charts, segments at the same index stack vertically
      let foundHigherSegment = false;
      
      // Check all datasets for segments at the same index
      for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex++) {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (meta && meta.data && meta.data[currentIndex]) {
          const segment = meta.data[currentIndex];
          if (segment && segment._view && segment !== this) {
            const segmentTop = segment._view.y;
            // In Chart.js, smaller y values are higher up (closer to top)
            // If we find a segment with a smaller y value, this is not the top segment
            if (segmentTop < currentTop - 0.1) {
              foundHigherSegment = true;
              break;
            }
          }
        }
      }
      
      isTopSegment = !foundHigherSegment;
    } else if (isStacked && vm.horizontal) {
      // For horizontal stacked bars, find all segments at the same y position
      // The top segment is the one with the largest x value (rightmost)
      const currentY = vm.y;
      const currentRight = right;
      const currentIndex = this._index;
      
      // Check all datasets for segments at the same index
      let foundRightSegment = false;
      
      for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex++) {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (meta && meta.data && meta.data[currentIndex]) {
          const segment = meta.data[currentIndex];
          if (segment && segment._view && segment !== this) {
            const segmentRight = segment._view.x + (segment._view.width || 0) / 2;
            // If we find a segment further to the right, this is not the top segment
            if (segmentRight > currentRight + 0.1) {
              foundRightSegment = true;
              break;
            }
          }
        }
      }
      
      isTopSegment = !foundRightSegment;
    }

    ctx.beginPath();
    ctx.fillStyle = vm.backgroundColor;
    ctx.strokeStyle = vm.borderColor;
    ctx.lineWidth = borderWidth;

    // Round corners based on bar orientation and whether it's the top segment
    if (!vm.horizontal) {
      // Vertical bar - round top corners only if it's the top segment or not stacked
      const shouldRoundTop = !isStacked || isTopSegment;
      const radius = shouldRoundTop ? Math.min(
        CHART_CORNER_RADIUS,
        Math.abs(top - bottom) / 2,
        Math.abs(right - left) / 2
      ) : 0;

      if (radius > 0) {
        // Draw with rounded top corners
        ctx.moveTo(left + radius, top);
        ctx.lineTo(right - radius, top);
        ctx.quadraticCurveTo(right, top, right, top + radius);
        ctx.lineTo(right, bottom);
        ctx.lineTo(left, bottom);
        ctx.lineTo(left, top + radius);
        ctx.quadraticCurveTo(left, top, left + radius, top);
      } else {
        // Draw rectangle without rounded corners
        ctx.rect(left, top, right - left, bottom - top);
      }
    } else {
      // Horizontal bar - round right corners only if it's the top segment or not stacked
      const shouldRoundRight = !isStacked || isTopSegment;
      const radius = shouldRoundRight ? Math.min(
        CHART_CORNER_RADIUS,
        Math.abs(top - bottom) / 2,
        Math.abs(right - left) / 2
      ) : 0;

      if (radius > 0) {
        // Draw with rounded right corners
        ctx.moveTo(left, top + radius);
        ctx.lineTo(left, bottom - radius);
        ctx.quadraticCurveTo(left, bottom, left + radius, bottom);
        ctx.lineTo(right - radius, bottom);
        ctx.quadraticCurveTo(right, bottom, right, bottom - radius);
        ctx.lineTo(right, top + radius);
        ctx.quadraticCurveTo(right, top, right - radius, top);
        ctx.lineTo(left + radius, top);
        ctx.quadraticCurveTo(left, top, left, top + radius);
      } else {
        // Draw rectangle without rounded corners
        ctx.rect(left, top, right - left, bottom - top);
      }
    }

    ctx.closePath();
    ctx.fill();
    if (borderWidth) {
      ctx.stroke();
    }
  };
}

/**
 * Plugin to create concentric circles for donut charts
 * When multiple datasets are present, each dataset gets a different radius
 * Outer ring (dataset 0) uses full radius, inner rings use progressively smaller radii
 */
export function applyConcentricDonutPlugin(): void {
  // Register plugin if Chart is available
  if (typeof Chart !== 'undefined' && Chart.plugins) {
    // Store original arc update function
    const originalArcUpdate = Chart.elements.Arc.prototype.update;

    Chart.elements.Arc.prototype.update = function(reset: boolean) {
      // Call original update first
      originalArcUpdate.call(this, reset);

      const chart = this._chart;
      const datasets = chart.data.datasets;

      // Only apply to doughnut/pie charts with multiple datasets
      if (chart.config.type !== 'doughnut' && chart.config.type !== 'pie') {
        return;
      }

      if (!datasets || datasets.length <= 1) {
        return;
      }

      const vm = this._view;
      const chartArea = chart.chartArea;
      const availableRadius = Math.min(
        (chartArea.right - chartArea.left) / 2,
        (chartArea.bottom - chartArea.top) / 2
      );

      // Calculate radius based on dataset index
      // Outer ring (dataset 0): full radius with 50% cutout
      // Inner ring (dataset 1): smaller radius with larger cutout
      if (this._datasetIndex === 0) {
        // Outer ring - use standard donut
        vm.outerRadius = availableRadius * 0.9;
        vm.innerRadius = availableRadius * 0.45; // 50% cutout
      } else if (this._datasetIndex === 1) {
        // Inner ring - smaller, nested inside outer ring
        vm.outerRadius = availableRadius * 0.4; // Smaller outer radius
        vm.innerRadius = availableRadius * 0.2; // Larger cutout for inner ring
      }
    };
  }
}

